// src/components/admin/services/ServiceModal.jsx
import { useState, useEffect } from "react";
import {
  X,
  Loader2,
  Save,
  Image as ImageIcon,
  Trash2,
  Upload,
  GripVertical,
} from "lucide-react";
import useAxiosAdmin from "../../lib/axiosAdmin";
import { toast } from "react-toastify";
import { SET_SHOULD_REFRESH_ADMIN_STAFF } from "../../../../redux/action/admin/staff/type";
import { useDispatch } from "react-redux";

const ServiceModal = ({ onClose, onSuccess, editService }) => {
  const axiosAdmin = useAxiosAdmin();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const dispatch = useDispatch()

  const [formData, setFormData] = useState({
    name: "",
    kasht: false,
    retoosh: false,
    tarmim: false,
    lift: false,
    normal: {
      enabled: false,
      price: "",
    },
    vip: {
      enabled: false,
      price: "",
    },
    kashtPrice: "",
    tarmimPrice: "",
    retooshPrice: "",
    liftPrice: "",
    images: [],
  });

  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (editService) {
      setFormData({
        name: editService.name || "",
        kasht: editService.kasht || false,
        retoosh: editService.retoosh || false,
        tarmim: editService.tarmim || false,
        lift: editService.lift || false,
        normal: {
          enabled: editService.normal?.enabled || false,
          price: editService.normal?.price || "",
        },
        vip: {
          enabled: editService.vip?.enabled || false,
          price: editService.vip?.price || "",
        },
        kashtPrice: editService.kashtPrice || "",
        tarmimPrice: editService.tarmimPrice || "",
        retooshPrice: editService.retooshPrice || "",
        liftPrice: editService.liftPrice || "",
        images: editService.images || [],
      });
    }
  }, [editService]);

  // تبدیل اعداد فارسی به انگلیسی
  const convertPersianToEnglish = (str) => {
    const persianNumbers = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    const englishNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

    let result = str;
    for (let i = 0; i < 10; i++) {
      result = result.replace(new RegExp(persianNumbers[i], "g"), englishNumbers[i]);
    }
    return result;
  };

  // اعتبارسنجی ورودی قیمت
  const handlePriceChange = (value, type) => {
    const persianEnglishNumbers = /[۰-۹0-9]/g;
    const filteredValue = (value.match(persianEnglishNumbers) || []).join("");
    const englishValue = convertPersianToEnglish(filteredValue);

    setFormData({
      ...formData,
      [type]: { ...formData[type], price: englishValue },
    });
  };

  // ✅ آپلود تصویر به سرور
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // بررسی حجم فایل (حداکثر 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("حجم فایل نباید بیشتر از ۵ مگابایت باشد");
      e.target.value = "";
      return;
    }

    // بررسی فرمت فایل
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("فقط فایل‌های JPG, PNG, GIF و WebP مجاز هستند");
      e.target.value = "";
      return;
    }

    // بررسی تعداد تصاویر
    if (formData.images.length >= 20) {
      toast.error("حداکثر ۲۰ تصویر مجاز است");
      e.target.value = "";
      return;
    }

    try {
      setUploadingImage(true);

      // ایجاد FormData برای آپلود
      const formDataUpload = new FormData();
      formDataUpload.append("image", file);

      // ارسال به سرور
      const response = await axiosAdmin.post(
        "/api/admin/upload/service-image",
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // اضافه کردن URL تصویر به لیست
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, response.data.imageUrl],
      }));

      toast.success("تصویر با موفقیت آپلود شد");
    } catch (error) {
      console.log(error);
      console.error("خطا در آپلود تصویر:", error);
      toast.error(error.response?.data?.message || "خطا در آپلود تصویر");
    } finally {
      setUploadingImage(false);
      // ریست کردن input
      e.target.value = "";
    }
  };

  // ✅ حذف تصویر (از state و سرور)
  const handleRemoveImage = async (index, imageUrl) => {
    try {
      // استخراج filename از URL
      const filename = imageUrl.split("/").pop();

      // حذف از سرور
      await axiosAdmin.delete(`/api/admin/upload/service-image/${filename}`);

      // حذف از state
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      }));

      toast.success("تصویر حذف شد");
    } catch (error) {
      console.error("خطا در حذف تصویر:", error);
      // حتی اگر از سرور حذف نشد، از state حذف کن
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      }));
      toast.warning("تصویر از لیست حذف شد");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("نام خدمت الزامی است");
      return;
    }

    if (formData.name.trim().length < 3) {
      toast.error("نام خدمت باید حداقل ۳ کاراکتر باشد");
      return;
    }

    if (!formData.kasht && !formData.retoosh && !formData.tarmim && !formData.lift) {
      toast.error("حداقل یک زیرخدمت را انتخاب کنید");
      return;
    }

    if (!formData.normal.enabled && !formData.vip.enabled) {
      toast.error("حداقل یک نوع ارائه (عادی یا VIP) را فعال کنید");
      return;
    }

    if (formData.normal.enabled && (!formData.normal.price || formData.normal.price <= 0)) {
      toast.error("قیمت ارائه عادی را وارد کنید");
      return;
    }

    if (formData.vip.enabled && (!formData.vip.price || formData.vip.price <= 0)) {
      toast.error("قیمت ارائه VIP را وارد کنید");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name.trim(),
        kasht: formData.kasht,
        retoosh: formData.retoosh,
        tarmim: formData.tarmim,
        lift: formData.lift,
        normal: {
          enabled: formData.normal.enabled,
          price: formData.normal.enabled ? Number(formData.normal.price) : 0,
        },
        vip: {
          enabled: formData.vip.enabled,
          price: formData.vip.enabled ? Number(formData.vip.price) : 0,
        },
        kashtPrice: Number(formData.kashtPrice) || 0,
        tarmimPrice: Number(formData.tarmimPrice) || 0,
        retooshPrice: Number(formData.retooshPrice) || 0,
        liftPrice: Number(formData.liftPrice) || 0,
        images: formData.images,
      };

      if (editService) {
        await axiosAdmin.put(`/api/admin/services/${editService._id}`, payload);
        toast.success("خدمت با موفقیت ویرایش شد");
      } else {
        await axiosAdmin.post("/api/admin/services", payload);
        toast.success("خدمت با موفقیت ایجاد شد");
      }

      onSuccess();
      onClose();

      dispatch({
        type: SET_SHOULD_REFRESH_ADMIN_STAFF,
        payload: true
      });

    } catch (err) {
      console.error("خطا در ذخیره خدمت:", err);
      toast.error(err.response?.data?.message || "خطا در ذخیره خدمت");
    } finally {
      setLoading(false);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...formData.images];
    const draggedItem = newImages[draggedIndex];

    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedItem);

    setFormData({ ...formData, images: newImages });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // رنگ‌های ثابت برای زیرخدمت‌ها (جلوگیری از purge در Tailwind)
  const subServiceColors = {
    kasht: { border: "border-green-500", bg: "bg-green-50" },
    retoosh: { border: "border-blue-500", bg: "bg-blue-50" },
    tarmim: { border: "border-amber-500", bg: "bg-amber-50" },
    lift: { border: "border-purple-500", bg: "bg-purple-50" },
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <span className="text-2xl">{editService ? "✏️" : "➕"}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {editService ? "ویرایش خدمت" : "افزودن خدمت جدید"}
              </h2>
              <p className="text-purple-100 text-sm mt-0.5">
                اطلاعات خدمت را با دقت وارد کنید
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={loading}
            className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group disabled:opacity-50"
          >
            <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* Form Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* نام خدمت */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                نام خدمت <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="مثال: میکروبلیدینگ ابرو"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-purple-400 focus:bg-white transition-all"
                disabled={loading}
              />
            </div>

            {/* زیرخدمت‌ها */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                زیرخدمت‌ها <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "kasht", label: "کاشت", emoji: "🌱" },
                  { key: "retoosh", label: "روتوش", emoji: "✨" },
                  { key: "tarmim", label: "ترمیم", emoji: "🔧" },
                  { key: "lift", label: "لیفت", emoji: "⬆️" },
                ].map((item) => (
                  <label
                    key={item.key}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      formData[item.key]
                        ? `${subServiceColors[item.key].border} ${subServiceColors[item.key].bg}`
                        : "border-slate-200 bg-slate-50 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData[item.key]}
                      onChange={(e) =>
                        setFormData({ ...formData, [item.key]: e.target.checked })
                      }
                      disabled={loading}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-base">{item.emoji}</span>
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* قیمت نمایشی */}
            {(formData.kasht || formData.retoosh || formData.tarmim || formData.lift) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {formData.kasht && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <label className="block text-xs text-green-700 mb-1">قیمت کاشت (تومان)</label>
                    <input
                      type="text"
                      value={formData.kashtPrice || ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^۰-۹0-9]/g, "");
                        setFormData({ ...formData, kashtPrice: convertPersianToEnglish(v) });
                      }}
                      placeholder="مثال: ۲۸۰۰۰۰۰"
                      className="w-full px-3 py-2 bg-white border border-green-300 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-green-500 transition-all"
                      disabled={loading}
                    />
                  </div>
                )}
                {formData.retoosh && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="block text-xs text-blue-700 mb-1">قیمت روتوش (تومان)</label>
                    <input
                      type="text"
                      value={formData.retooshPrice || ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^۰-۹0-9]/g, "");
                        setFormData({ ...formData, retooshPrice: convertPersianToEnglish(v) });
                      }}
                      placeholder="مثال: ۱۶۰۰۰۰۰"
                      className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500 transition-all"
                      disabled={loading}
                    />
                  </div>
                )}
                {formData.tarmim && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <label className="block text-xs text-amber-700 mb-1">قیمت ترمیم (تومان)</label>
                    <input
                      type="text"
                      value={formData.tarmimPrice || ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^۰-۹0-9]/g, "");
                        setFormData({ ...formData, tarmimPrice: convertPersianToEnglish(v) });
                      }}
                      placeholder="مثال: ۱۶۰۰۰۰۰"
                      className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-amber-500 transition-all"
                      disabled={loading}
                    />
                  </div>
                )}
                {formData.lift && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <label className="block text-xs text-purple-700 mb-1">قیمت لیفت (تومان)</label>
                    <input
                      type="text"
                      value={formData.liftPrice || ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^۰-۹0-9]/g, "");
                        setFormData({ ...formData, liftPrice: convertPersianToEnglish(v) });
                      }}
                      placeholder="مثال: ۵۰۰۰۰۰"
                      className="w-full px-3 py-2 bg-white border border-purple-300 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-purple-500 transition-all"
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            )}

            {/* نوع ارائه - عادی */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base">💼</span>
                  <label className="text-sm font-medium text-slate-700">ارائه عادی</label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.normal.enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        normal: { ...formData.normal, enabled: e.target.checked },
                      })
                    }
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {formData.normal.enabled && (
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">قیمت (تومان)</label>
                  <input
                    type="text"
                    value={formData.normal.price}
                    onChange={(e) => handlePriceChange(e.target.value, "normal")}
                    placeholder="مثال: ۸۰۰۰۰۰"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-purple-400 transition-all"
                    disabled={loading}
                  />
                  <p className="text-sm mt-1.5 text-slate-600">
                    تعیین شد:{" "}
                    <span className="font-medium text-slate-800">
                      {Number(formData.normal.price)?.toLocaleString("fa")} تومان
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* نوع ارائه - VIP */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base">👑</span>
                  <label className="text-sm font-medium text-slate-700">ارائه VIP</label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.vip.enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        vip: { ...formData.vip, enabled: e.target.checked },
                      })
                    }
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {formData.vip.enabled && (
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">قیمت (تومان)</label>
                  <input
                    type="text"
                    value={formData.vip.price}
                    onChange={(e) => handlePriceChange(e.target.value, "vip")}
                    placeholder="مثال: ۱۲۰۰۰۰۰"
                    className="w-full px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-purple-400 transition-all"
                    disabled={loading}
                  />
                  <p className="text-sm mt-1.5 text-slate-600">
                    تعیین شد:{" "}
                    <span className="font-medium text-slate-800">
                      {Number(formData.vip.price)?.toLocaleString("fa")} تومان
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* مدیریت تصاویر */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🖼️</span>
                <label className="block text-sm font-medium text-slate-700">
                  تصاویر خدمت ({formData.images.length}/20)
                </label>
              </div>

              {/* ✅ آپلود تصویر */}
              <div className="mb-4">
                <label
                  htmlFor="image-upload"
                  className={`flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                    uploadingImage || formData.images.length >= 20
                      ? "border-slate-300 bg-slate-100 cursor-not-allowed"
                      : "border-purple-300 bg-purple-50 hover:border-purple-400 hover:bg-purple-100"
                  }`}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                      <span className="text-sm font-medium text-purple-700">
                        در حال آپلود...
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-700">
                        {formData.images.length >= 20
                          ? "حداکثر تعداد تصاویر"
                          : "انتخاب و آپلود تصویر"}
                      </span>
                    </>
                  )}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploadingImage || loading || formData.images.length >= 20}
                  className="hidden"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  فرمت‌های مجاز: JPG, PNG, GIF, WebP | حداکثر حجم: ۵ مگابایت
                </p>
              </div>

              {/* لیست تصاویر */}
              {formData.images.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto p-1">
                  {formData.images.map((url, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`relative group bg-slate-50 border border-slate-200 rounded-lg p-2 cursor-move transition-all ${
                        draggedIndex === index
                          ? "opacity-50 scale-95"
                          : "hover:border-purple-300 hover:bg-purple-50/30"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />

                        {/* ✅ پیش‌نمایش تصویر */}
                        <div className="w-24 h-24 rounded border border-slate-200 overflow-hidden flex-shrink-0 bg-slate-100">
                          <img
                            src={ (url?.includes('https://') || url?.includes('http://') )  ? url : 'https://my-sarad-back.liara.run'+url}
                            alt={`تصویر ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect fill='%23e2e8f0' width='64' height='64'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='monospace' font-size='12' fill='%2394a3b8'%3EError%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-600 truncate">{url}</p>
                          <p className="text-xs text-slate-400 mt-0.5">تصویر {index + 1}</p>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index, url)}
                          disabled={loading}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors sm:opacity-0 group-hover:opacity-100 disabled:opacity-50 flex-shrink-0"
                          title="حذف تصویر"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">هنوز تصویری اضافه نشده</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions - Fixed */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              لغو
            </button>

            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  در حال ذخیره...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {editService ? "ذخیره تغییرات" : "ایجاد خدمت"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceModal;